/**
 * Shared "Claim verification" section for every phased worker/analyst
 * bundle (fleet anti-collision — codedrifters/packages#868).
 *
 * Fleet deployments run several orchestrator runners against one shared
 * GitHub queue (staggered scheduled tasks over isolated clones). A bare
 * `status:ready` → `status:in-progress` label flip does not, on its own,
 * guarantee that only one session works an issue: two runners can scan
 * the queue in the same window and both flip the label. The orchestrator
 * closes the common multi-minute window with **claim-at-pick** (it claims
 * the label itself before dispatching); this section is the dispatched
 * agent's **verify-or-claim backstop** for the sub-second residual race.
 *
 * The section is rendered identically into the issue-worker and every
 * phased analyst/writer prompt via {@link renderClaimVerificationSection}
 * so the protocol never drifts between bundles — the same pattern the
 * project-context and progress-file sections use.
 */

/**
 * Greppable HTML-comment tag that anchors the claim-marker comment.
 * Exported so callers (orchestrator claim-at-pick, tests) reference the
 * exact same string the verify query matches on.
 */
export const ISSUE_CLAIM_MARKER_TAG = "<!-- issue-claim -->";

/**
 * jq filter (for `gh issue view <n> --json comments`) that lists every
 * issue-claim marker in claim order (earliest first, tie-broken on the
 * lowest comment id). The earliest marker is the owner.
 */
export const CLAIM_MARKER_LIST_JQ = `[.comments[] | select(.body | contains("${ISSUE_CLAIM_MARKER_TAG}")) | {id, createdAt}] | sort_by(.createdAt, .id)`;

/**
 * Render the shared `## Claim verification` section as prompt lines.
 * Spread into a sub-agent `prompt` array with
 * `...renderClaimVerificationSection()`.
 */
export function renderClaimVerificationSection(): ReadonlyArray<string> {
  return [
    "## Claim verification",
    "",
    "Before you create a branch or write any artifacts, verify you own the",
    "claim on the issue. This governs the `status:ready` →",
    "`status:in-progress` transition. In fleet deployments several",
    "orchestrator runners share one GitHub queue, so a bare label flip does",
    "not by itself guarantee you are the only session working the issue —",
    "two runners can scan the queue in the same window and both claim it",
    "(fleet anti-collision).",
    "",
    "### The issue-claim marker",
    "",
    "Ownership is recorded by a single claim-marker comment on the issue.",
    "Whoever claims the issue — the orchestrator at pick time",
    "(claim-at-pick), or you, when you claim an unclaimed issue yourself —",
    "posts exactly one:",
    "",
    "```",
    `${ISSUE_CLAIM_MARKER_TAG} Claimed <ISO-8601 UTC> by <claimant> — fleet anti-collision.`,
    "```",
    "",
    "The **owner is the earliest** claim marker — the",
    `\`${ISSUE_CLAIM_MARKER_TAG}\` comment with the lowest \`createdAt\``,
    "(tie-broken on the lowest comment id). List the markers in claim order",
    "with:",
    "",
    "```bash",
    "gh issue view <n> --json comments \\",
    `  --jq '${CLAIM_MARKER_LIST_JQ}'`,
    "```",
    "",
    "### Verify or claim",
    "",
    "1. **Pre-claimed at pick.** If your dispatch brief says the orchestrator",
    "   already flipped the label (claim-at-pick) and cites the marker it",
    "   posted, confirm that marker is the **earliest** claim marker on the",
    "   issue, then proceed. Do **not** re-add `status:ready` and do **not**",
    "   re-flip the label — the claim is already yours.",
    "2. **Unclaimed (`status:ready`).** Claim it yourself: **first** post the",
    `   \`${ISSUE_CLAIM_MARKER_TAG}\` marker comment, **then** flip`,
    "   `status:ready` → `status:in-progress`. Re-read the markers; proceed",
    "   only if the marker you just posted is the earliest.",
    "3. **Foreign prior claim → back off.** If a claim marker from another",
    "   dispatch is **earlier** than yours — or the issue is already",
    "   `status:in-progress` with a foreign marker and your dispatch brief",
    "   did not claim it for you — a sibling runner owns the issue. **Back",
    "   off cleanly and write no artifacts**: no branch, no files, no PR, and",
    "   no completion or duplicate-output comment. Log",
    "   `CLAIM_CONFLICT #<n> owner=<earliest-marker-id>` and stop the session.",
    "",
    "Backing off on a foreign claim is the analyst-side backstop to the",
    "orchestrator's claim-at-pick guard: claim-at-pick closes the common",
    "multi-minute window between the queue scan and the claim, and this",
    "check absorbs the sub-second residual race where two runners both",
    "scanned before either flipped the label. If you discover a foreign",
    "prior claim **mid-execution** — after you have already begun a phase —",
    "stop immediately and discard the uncommitted work rather than opening a",
    "second PR or writing a duplicate artifact for the issue.",
  ];
}
